import { Box } from "@mui/material";
import { useEffect, useState } from "react";
import {
    List,
    Datagrid,
    TextField,
    TextInput,
    TopToolbar,
    DateInput,
    SelectInput,
    DateField,
    useDataProvider,
    useListContext,
    Button,
    useNotify,
    FunctionField,
    ShowButton
} from "react-admin";

const ListActions = () => {
    const dataProvider = useDataProvider(); // 获取数据提供者
    const notify = useNotify(); // 获取通知方法
    const { setFilters, filterValues } = useListContext();  // 获取列表上下文
    const initialFilters = {};  // 初始过滤器值

    // 重置过滤器函数
    const handleResetFilters = () => {
        setFilters(initialFilters);
    };

    const exportDetails = () => {
        if (window.confirm(`确认导出设备流量收益数据？`)) {
            const formData = {
                ...filterValues,
            };
            dataProvider.trafficIncomeExport(formData)
                .then(() => {
                    notify('导出设备流量收益数据成功', { type: 'success' });
                })
                .catch((error: any) => {
                    notify(error.message || '导出设备流量收益数据失败', { type: 'error' });
                })
        }
    }

    return (
        <TopToolbar>
            <Button
                label="重置搜索"
                onClick={handleResetFilters}
                disabled={Object.keys(filterValues).length === 0}  // 当没有过滤条件时禁用
            />
            <Button
                label="导出"
                onClick={exportDetails}
            />
        </TopToolbar>
    );
};


export const trafficRevenue = () => {
    const dataProvider = useDataProvider(); // 获取dataProvider
    const [customerTypeChoices, setSaleCustomers] = useState([]);
    const [trafficCustomers, setTrafficCustomers] = useState([]); // 流量商家数据
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // 异步获取客户类型数据
        const fetchCustomers = async () => {
            try {
                setLoading(true);
                // 同时获取两种类型的客户数据
                const [salesData, trafficData] = await Promise.all([
                    dataProvider.getSalesCustomers(),
                    dataProvider.getTrafficCustomers()
                ]);

                setSaleCustomers(salesData.map(item => ({
                    id: item.id,
                    name: item.name
                })));

                setTrafficCustomers(trafficData.map(item => ({
                    id: item.id,
                    name: item.name
                })));
            } catch (error) {
                console.error("获取下拉列表失败:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchCustomers();
    }, []);  // 空依赖数组确保只调用一次


    let postFilters = [
        <SelectInput
            alwaysOn
            source="salesCustomerIds"
            label="设备商家"
            choices={customerTypeChoices}
            disabled={loading}
        />,
        <SelectInput
            alwaysOn
            source="trafficCustomerIds"
            label="流量商家"
            choices={trafficCustomers}
            disabled={loading}
        />,
        <TextInput label="设备SN" alwaysOn source="sn" />,
        <DateInput label="开始日期" alwaysOn source="startDate" />,
        <DateInput label="结束日期" alwaysOn source="endDate" />,
    ];


    return (
        <List filters={postFilters} actions={<ListActions />} empty={false}>
            <Datagrid bulkActionButtons={false} rowClick={false}>
                <TextField source="id" label="序号" sortable={false} sx={{
                    whiteSpace: 'nowrap',
                }} />
                <TextField source="sn" label="设备SN" sortable={false} sx={{
                    whiteSpace: 'nowrap',
                }} />
                {/* <TextField source="ram" label="昨日收益" sortable={false}/> */}
                <FunctionField
                    label="累计收益"
                    source="totalIncome"
                    render={record => (record.totalIncome || 0) + '元'}
                    sortable={false}
                    sx={{
                        whiteSpace: 'nowrap',
                    }}
                />
                <FunctionField
                    label="累计流量"
                    source="totalTraffic"
                    render={record => {
                        // 确保值存在且为数字
                        const value = Number(record.totalTraffic);
                        if (isNaN(value)) return '0.000 TB';

                        // 将字节转换为TB并保留三位小数
                        const tb = (value / (1024 * 1024 * 1024 * 1024)).toFixed(3);
                        return `${tb} TB`;
                    }}
                    sortable={false}
                />
                <TextField source="trafficCustomerName" label="流量商家" sortable={false} sx={{
                    whiteSpace: 'nowrap',
                }} />
                <TextField source="salesCustomerName" label="设备商家" sortable={false} sx={{
                    whiteSpace: 'nowrap',
                }} />

                <DateField source="activeTime" label="用户绑定日期" sortable={false} sx={{
                    whiteSpace: 'nowrap',
                }} />
                <FunctionField
                    label="流量累计时长"
                    source="totalHours"
                    render={record => {
                        const totalSeconds = record.totalDuration;
                        const days = Math.floor(totalSeconds / 86400);
                        const hours = Math.floor((totalSeconds % 86400) / 3600);

                        if (days > 0) {
                            return `${days}天${hours}小时`;
                        } else {
                            return `${hours}小时`;
                        }
                    }}
                    sortable={false}
                />
                <FunctionField
                    label="设备状态"
                    source="status"
                    render={record => record.status == 1 ? '在线' : '离线'}
                    sortable={false}
                />
                <FunctionField
                    label="设备位置"
                    source="city"
                    render={record =>
                        (record.province || '').replace(/省$/, '') +
                        ' ' +
                        (record.city || '').replace(/市$/, '')
                    }
                    sortable={false}
                />
                <TextField source="operator" label="运营商" sortable={false} sx={{
                    whiteSpace: 'nowrap',
                }} />
                <TextField source="natType" label="NAT类型" sortable={false} sx={{
                    whiteSpace: 'nowrap',
                }} />

                <FunctionField
                    label="上行网速"
                    source="uploadSpeed"
                    render={record => (record.uploadSpeed || 0) + 'Mbps'}
                    sortable={false}
                    sx={{
                        whiteSpace: 'nowrap',
                    }}
                />
                <FunctionField
                    label="操作"
                    render={(record) => {
                        return (
                            <Box sx={{ justifyContent: 'center', display: 'flex', gap: '8px' }}>
                                <ShowButton
                                    label="详情"
                                    record={{ id: record.sn }}
                                />
                            </Box>
                        );
                    }}
                    sortable={false}
                />
            </Datagrid>
            <div style={{ margin: '20px 10px' }}>说明：权益管理表每日14时前更新数据。</div>
        </List>
    )
}